/**
 * @file
 * React hook reacting to navigation away of the Trip builder
 *
 * @format
 * @flow strict-local
 */

import React, { useCallback, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';

import Dialog from '@appComponents/Dialog';
import Text from '@appComponents/Text';
import Button from '@appComponents/Button';
import { Spacer } from '@appComponents/ScreenLayout';

type confirmationConfig = {
  hasUnsavedChanges: boolean,
  canSave: boolean,
  save: () => void,
  init: () => void,
};

const useExitTripBuilderConfirmation = (
  state: confirmationConfig,
  navigation: NavigationProp,
) => {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [screenVisible, setScreenVisible] = useState(false);
  const builderState = useRef();
  builderState.current = state;
  const navState = useRef();

  const saveChanges = useCallback(async () => {
    setConfirmVisible(false);
    await builderState.current.save();
  }, []);

  const discardChanges = useCallback(() => setConfirmVisible(false), []);

  const goBack = useCallback(() => {
    navigation.reset(navState.current);
    navState.current = null;
  }, [navigation]);

  const focusEffect = useCallback(() => {
    // Re-initialize builder on open, unless it's from "go back"
    if (navState.current) {
      builderState.current.init();
    }

    // Capture current nav state in case we need to restore it with "go back"
    navState.current = navigation.getState();
    setConfirmVisible(false);
    setScreenVisible(true);

    return () => {
      setScreenVisible(false);

      if (builderState.current.hasUnsavedChanges) {
        setConfirmVisible(true);
      }
    };
  }, [navigation]);

  useFocusEffect(focusEffect);

  const confirmationNode = (
    <Dialog
      title="Unsaved Changes"
      visible={Boolean(confirmVisible)}
      dismissable={false}
      actionSlot={
        <>
          <Button mode="outlined" color="dark" onPress={goBack}>
            Go Back
          </Button>
          <Spacer dir="horizontal" />

          <Button mode="outlined" color="error" onPress={discardChanges}>
            Discard Changes
          </Button>
          <Spacer dir="horizontal" />

          {state.canSave && (
            <>
              <Button onPress={saveChanges}>Save and Send</Button>
              <Spacer dir="horizontal" />
            </>
          )}
        </>
      }>
      {state.canSave ? (
        <Text>
          You have unsaved changes on this trip. Do you want to save and send
          these changes?
        </Text>
      ) : (
        <Text>{buildRequiredFieldsMessage({ state })}</Text>
      )}
    </Dialog>
  );

  return { screenVisible, confirmationNode };
};

const buildRequiredFieldsMessage = ({ state }) => {
  const missingOwner = !state?.trip?.owner;
  const missingName = !state?.trip?.customName;
  const missingFrom = isEmpty(state?.trip?.legs) || !state?.trip?.legs[0]?.from;
  const missingTo = isEmpty(state?.trip?.legs) || !state?.trip?.legs[0]?.to;

  let isFirst = true;
  let atLeastTwo = false;
  let message =
    "This trip doesn't have the minimum information needed to save it. If you want to save this trip go back and ";
  if (missingOwner) {
    isFirst = false;
    message += 'select a client';
  }
  if (missingName) {
    const isLast = !missingFrom && !missingTo;
    if (!isFirst && isLast) {
      message += ' and ';
    } else if (!isFirst && !isLast) {
      message += ', ';
      atLeastTwo = true;
    }
    isFirst = false;
    message += 'enter a trip name';
  }
  if (missingFrom) {
    const isLast = !missingTo;
    if (!isFirst && isLast) {
      if (atLeastTwo) {
        message += ',';
      }
      message += ' and ';
    } else if (!isFirst && !isLast) {
      message += ', ';
      atLeastTwo = true;
    }
    isFirst = false;
    message += 'enter a departure airport';
  }
  if (missingTo) {
    if (!isFirst) {
      if (atLeastTwo) {
        message += ',';
      }
      message += ' and ';
    }
    message += 'enter an arrival airport';
  }

  message += '.';
  return message;
};

export default useExitTripBuilderConfirmation;
